/**
 * SEO Campaign Tracking Utility
 * 
 * Handles storing UTM / Ad campaign query parameters in localStorage (as a time-based array),
 * extracting the latest (last index) tracking data to send in signup & login APIs,
 * and redirecting the user back to their source landing page after signup/login.
 */

export const CAMPAIGN_STORAGE_KEY = 'seo_campaign_history';
export const CAMPAIGN_REDIRECT_KEY = 'seo_campaign_redirect_url';

/**
 * Safely parse query parameters from location or URL
 * @param {Object|string} locationObj 
 * @returns {Object} key-value pairs of all query parameters
 */
export const extractQueryParams = (locationObj) => {
  try {
    let search = '';
    if (typeof locationObj === 'string') {
      const qIndex = locationObj.indexOf('?');
      search = qIndex !== -1 ? locationObj.substring(qIndex) : locationObj;
    } else if (locationObj && typeof locationObj.search === 'string') {
      search = locationObj.search;
    } else if (typeof window !== 'undefined' && window.location) {
      search = window.location.search || '';
    }

    if (!search || search.trim() === '' || search === '?') {
      return {};
    }

    const searchParams = new URLSearchParams(search);
    const params = {};
    for (const [key, value] of searchParams.entries()) {
      if (key && key.trim()) {
        params[key.trim()] = value !== undefined ? value.trim() : '';
      }
    }
    return params;
  } catch (error) {
    console.error('Error extracting query params:', error);
    return {};
  }
};

/**
 * Save campaign tracking data to localStorage array
 * @param {Object} locationObj - react-router location object or window.location
 * @returns {Object|null} The stored entry or null if no parameters found
 */
export const saveCampaignTracking = (locationObj) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }

    const pathname = locationObj?.pathname || window.location.pathname || '';
    const search = locationObj?.search || window.location.search || '';

    // Ignore auth pages from being tracked as campaign landing pages
    if (
      pathname.startsWith('/login') ||
      pathname.startsWith('/signup') ||
      pathname.startsWith('/password-reset')
    ) {
      return null;
    }

    const params = extractQueryParams(search);
    if (!params || Object.keys(params).length === 0) {
      return null;
    }

    const fullUrl = window.location.href || (pathname + search);
    const path = pathname + search;
    const now = Date.now();

    const entry = {
      url: fullUrl,
      path: path,
      pathname: pathname,
      search: search,
      params: params,
      timestamp: now,
      time: new Date(now).toISOString(),
      ...params,
    };

    // Retrieve existing array from localStorage
    let history = getAllCampaignTracking();

    // Check duplicate: if user refreshed the exact same page within 60 seconds
    const lastItem = history.length > 0 ? history[history.length - 1] : null;
    const isDuplicate =
      lastItem &&
      lastItem.path === entry.path &&
      JSON.stringify(lastItem.params) === JSON.stringify(entry.params) &&
      now - (lastItem.timestamp || 0) < 60000;

    if (isDuplicate) {
      // Update the timestamp of the last entry
      history[history.length - 1] = {
        ...lastItem,
        timestamp: now,
        time: entry.time,
      };
    } else {
      // Append the new ad visit
      history.push(entry);

      // Keep maximum 50 entries to avoid localStorage overflow
      if (history.length > 50) {
        history = history.slice(history.length - 50);
      }
    }

    window.localStorage.setItem(CAMPAIGN_STORAGE_KEY, JSON.stringify(history));
    window.localStorage.setItem(CAMPAIGN_REDIRECT_KEY, path);

    return entry;
  } catch (error) {
    console.error('Error saving campaign tracking data:', error);
    return null;
  }
};

/**
 * Get all stored campaign tracking entries
 * @returns {Array} List of campaign tracking entries
 */
export const getAllCampaignTracking = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return [];
    }
    const raw = window.localStorage.getItem(CAMPAIGN_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Error reading campaign tracking history:', error);
    return [];
  }
};

/**
 * Get the latest (last index) campaign tracking entry
 * @returns {Object|null}
 */
export const getLastCampaignTracking = () => {
  try {
    const history = getAllCampaignTracking();
    if (!history || history.length === 0) {
      return null;
    }
    return history[history.length - 1] || null;
  } catch (error) {
    console.error('Error reading last campaign tracking item:', error);
    return null;
  }
};

/**
 * Get the payload fields to send in signup and login APIs
 * Returns an object with all query params from the last index campaign,
 * plus campaign_url / utm_link for full traceability.
 * If no campaign data is stored, returns an empty object {}.
 * @returns {Object}
 */
export const getCampaignApiPayload = () => {
  try {
    const lastCampaign = getLastCampaignTracking();
    if (!lastCampaign || !lastCampaign.params || Object.keys(lastCampaign.params).length === 0) {
      return {};
    }

    const payload = {
      ...lastCampaign.params,
    };

    const targetUrl = lastCampaign.path || lastCampaign.url;
    if (targetUrl) {
      payload.campaign_url = lastCampaign.url || lastCampaign.path;
      payload.utm_link = targetUrl;
      payload.link = targetUrl;
    }

    return payload;
  } catch (error) {
    console.error('Error preparing campaign API payload:', error);
    return {};
  }
};

/**
 * Get the redirect URL where the user came from
 * @returns {string|null}
 */
export const getCampaignRedirectUrl = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    const redirectUrl = window.localStorage.getItem(CAMPAIGN_REDIRECT_KEY);
    if (redirectUrl && redirectUrl.trim() !== '') {
      return redirectUrl.trim();
    }
    return null;
  } catch (error) {
    console.error('Error getting campaign redirect URL:', error);
    return null;
  }
};

/**
 * Clear the pending redirect URL after redirecting
 */
export const clearCampaignRedirectUrl = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(CAMPAIGN_REDIRECT_KEY);
    }
  } catch (error) {
    console.error('Error clearing campaign redirect URL:', error);
  }
};

/**
 * Clear all campaign tracking data (e.g. for testing / debugging)
 */
export const clearAllCampaignTracking = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(CAMPAIGN_STORAGE_KEY);
      window.localStorage.removeItem(CAMPAIGN_REDIRECT_KEY);
    }
  } catch (error) {
    console.error('Error clearing all campaign tracking:', error);
  }
};
